/**
 * Curated editorial content per country.
 * Used to enrich /cuisines-du-monde/:countrySlug pages with SEO-rich, original copy.
 * Always include "diabète" / "IG bas" semantic.
 */
export const COUNTRY_EDITORIAL = {
  France: {
    flag: "🇫🇷",
    tagline: "Le terroir français, IG bas et diabète maîtrisé",
    intro:
      "De la blanquette bourguignonne au clafoutis limousin, la cuisine française regorge de plats parfaitement adaptables aux personnes diabétiques. En remplaçant farines blanches, sucres raffinés et féculents à IG élevé par des alternatives à index glycémique bas, on conserve l'âme du terroir sans compromettre la glycémie.",
    pillars: [
      "Farines complètes T150 et sarrasin",
      "Cuissons longues qui développent les saveurs",
      "Huiles d'olive et noisettes en remplacement des graisses saturées",
      "Édulcorants naturels (érythritol, stévia) pour les pâtisseries",
    ],
    seoKeywords: [
      "recettes françaises diabétiques",
      "cuisine française IG bas",
      "plats traditionnels diabète",
      "gastronomie française diabétique",
    ],
  },
  Italie: {
    flag: "🇮🇹",
    tagline: "La cuisine italienne, IG bas pour les diabétiques",
    intro:
      "Contrairement aux idées reçues, la cuisine italienne authentique est très bien adaptée aux personnes diabétiques. Les vraies recettes — sans crème, sans sucre caché — se contentent de quelques ajustements simples : pâtes complètes al dente, huile d'olive en abondance, et fromages affinés en doses raisonnables. Pizza, carbonara, risotto, tiramisu — tout reste possible en IG bas.",
    pillars: [
      "Pâtes complètes strictement al dente (IG 40 vs 70 trop cuites)",
      "Risotto au riz Carnaroli + beaucoup de légumes",
      "Pizza sur base de farine complète et levain",
      "Tiramisu à l'érythritol et café fort",
    ],
    seoKeywords: [
      "recettes italiennes diabétiques",
      "cuisine italienne IG bas",
      "pâtes diabète",
      "pizza IG bas diabétique",
      "risotto diabète",
    ],
  },
  Maroc: {
    flag: "🇲🇦",
    tagline: "La cuisine marocaine, IG bas pour les diabétiques",
    intro:
      "La cuisine marocaine traditionnelle est naturellement riche en légumes, épices anti-inflammatoires et cuissons mijotées — un terrain idéal pour les personnes diabétiques. Tajines, harira, kefta, méchoui : ces plats du Maghreb gagnent en pertinence dès qu'on remplace le couscous blanc par du couscous complet ou du quinoa, et qu'on évite les pâtisseries au miel et au sucre raffiné.",
    pillars: [
      "Couscous complet ou quinoa au lieu du couscous blanc",
      "Cannelle et fenugrec, alliés naturels de la glycémie",
      "Tajines mijotés longuement, légumes en quantité",
      "Pâtisseries adaptées à l'érythritol et eau de fleur d'oranger",
    ],
    seoKeywords: [
      "recettes marocaines diabétiques",
      "tajine IG bas diabète",
      "cuisine marocaine diabète",
      "couscous diabétique",
      "harira diabète",
    ],
  },
  Inde: {
    flag: "🇮🇳",
    tagline: "La cuisine indienne, IG bas pour les diabétiques",
    intro:
      "L'Inde compte le plus grand nombre de personnes diabétiques au monde — mais aussi l'une des cuisines les mieux adaptées : abondance de légumineuses, légumes verts, épices régulatrices de glycémie (curcuma, fenugrec, cannelle). Dahl, sabzi, raïta, paneer aux épinards : la cuisine indienne traditionnelle, lorsqu'elle évite les naans blancs et les desserts ultra-sucrés, est un modèle pour diabétiques.",
    pillars: [
      "Lentilles corail, lentilles noires (IG 25-30)",
      "Riz basmati complet au lieu de riz blanc",
      "Curcuma : améliore la sensibilité à l'insuline",
      "Yaourt et raïta : ralentissent l'absorption des glucides",
    ],
    seoKeywords: [
      "recettes indiennes diabétiques",
      "dahl IG bas diabète",
      "cuisine indienne diabète",
      "curry diabétique",
      "lentilles indiennes diabète",
    ],
  },
  Espagne: {
    flag: "🇪🇸",
    tagline: "La cuisine espagnole, IG bas pour les diabétiques",
    intro:
      "Tapas, gazpacho, paella aux légumes, tortilla espagnole : la cuisine ibérique offre une variété surprenante de plats diabéto-compatibles. Riche en huile d'olive, légumes méditerranéens, poissons et fruits de mer, elle s'adapte naturellement à un régime à index glycémique bas. Le seul piège : éviter le riz blanc trop cuit dans les paellas et préférer le riz bomba al dente ou des féculents complets.",
    pillars: [
      "Huile d'olive vierge extra, pilier méditerranéen",
      "Légumes du soleil : poivrons, tomates, aubergines",
      "Poissons et fruits de mer en abondance",
      "Vinaigre de Xérès : abaisse la réponse glycémique",
    ],
    seoKeywords: [
      "recettes espagnoles diabétiques",
      "gazpacho diabète IG bas",
      "cuisine espagnole diabète",
      "paella diabétique",
      "tapas diabète",
    ],
  },
  Liban: {
    flag: "🇱🇧",
    tagline: "La cuisine libanaise, IG bas pour les diabétiques",
    intro:
      "La cuisine libanaise est souvent citée comme l'une des plus saines au monde — et pour cause : abondance de légumes crus, légumineuses, huile d'olive, herbes fraîches et grillades. Le mezze libanais (houmous, taboulé, baba ganoush, fatouche) est un modèle de repas IG bas pour personne diabétique. Quelques ajustements suffisent pour le pain libanais blanc et certains desserts.",
    pillars: [
      "Houmous, moutabal, baba ganoush : entrées à IG ~25",
      "Taboulé authentique : 90% persil, 10% boulgour complet",
      "Grillades simples, viande maigre et marinades au citron",
      "Pain libanais au levain complet en remplacement du pain blanc",
    ],
    seoKeywords: [
      "recettes libanaises diabétiques",
      "houmous IG bas diabète",
      "cuisine libanaise diabète",
      "mezze diabète",
      "taboulé diabétique",
    ],
  },
  Japon: {
    flag: "🇯🇵",
    tagline: "La cuisine japonaise, IG bas pour les diabétiques",
    intro:
      "La cuisine japonaise traditionnelle (washoku) est souvent classée parmi les plus diabéto-compatibles : faible en graisses saturées, riche en poissons, légumes fermentés et algues. Sushis, sashimis, soupe miso, donburi : tout s'adapte facilement en remplaçant le riz blanc par du riz brun ou du konjac, et en limitant les sauces sucrées (teriyaki).",
    pillars: [
      "Konjac et shirataki : féculents quasi sans glucides",
      "Algues (wakame, nori) : minéraux et fibres",
      "Soupe miso : fermentation, bonne pour le microbiote",
      "Sashimi et poissons gras : oméga-3 abondants",
    ],
    seoKeywords: [
      "recettes japonaises diabétiques",
      "sushi IG bas diabète",
      "cuisine japonaise diabète",
      "miso diabète",
      "japonais diabétique",
    ],
  },
  Mexique: {
    flag: "🇲🇽",
    tagline: "La cuisine mexicaine, IG bas pour les diabétiques",
    intro:
      "La cuisine mexicaine authentique (avant l'américanisation) est riche en haricots, maïs, piments, avocat et lime — autant d'aliments à index glycémique modéré ou bas. Tacos, guacamole, fajitas, chili : adaptés avec des tortillas de maïs complet ou de blé complet, ils restent parfaitement diabéto-compatibles.",
    pillars: [
      "Haricots noirs, rouges, pinto : protéines et fibres",
      "Avocat : graisses saines qui ralentissent la glycémie",
      "Tortillas de maïs complet (IG 40 vs 65 blanches)",
      "Lime et coriandre, accompagnements détox naturels",
    ],
    seoKeywords: [
      "recettes mexicaines diabétiques",
      "tacos IG bas diabète",
      "cuisine mexicaine diabète",
      "guacamole diabète",
      "chili diabétique",
    ],
  },
  Chine: {
    flag: "🇨🇳",
    tagline: "La cuisine chinoise, IG bas pour les diabétiques",
    intro:
      "La cuisine chinoise traditionnelle, riche en légumes sautés au wok, tofu, poissons et bouillons, peut être un excellent allié du diabétique — à condition d'éviter le riz blanc raffiné et les sauces sucrées du commerce. Sautés rapides, vapeur, congee de riz brun : les techniques chinoises préservent les nutriments et minimisent l'impact glycémique.",
    pillars: [
      "Wok : cuisson rapide, légumes croquants, IG conservé bas",
      "Riz brun et nouilles de soba (sarrasin) au lieu du riz blanc",
      "Tofu et viandes maigres en abondance",
      "Sauce soja légère + vinaigre de riz : pas de sucre caché",
    ],
    seoKeywords: [
      "recettes chinoises diabétiques",
      "cuisine chinoise IG bas",
      "wok diabète",
      "sauté légumes diabète",
      "tofu diabétique",
    ],
  },
  Thaïlande: {
    flag: "🇹🇭",
    tagline: "La cuisine thaïlandaise, IG bas pour les diabétiques",
    intro:
      "La cuisine thaïlandaise équilibre acide, salé, sucré, épicé — un quatuor qui rassasie vite et limite les portions. Tom yum, salades de papaye verte, curry au lait de coco, satay : ces plats utilisent peu de féculents et beaucoup d'aromates anti-inflammatoires. Préférez les currys aux légumes et le riz au jasmin complet pour rester en IG bas.",
    pillars: [
      "Citronnelle, galanga, basilic thaï : aromates précieux",
      "Lait de coco riche en bons gras qui ralentissent la glycémie",
      "Riz au jasmin complet (IG 50) ou nouilles de riz al dente",
      "Sauces sucrées du commerce à éviter : sucre de coco maison à dose modérée",
    ],
    seoKeywords: [
      "recettes thaïlandaises diabétiques",
      "cuisine thaï IG bas",
      "curry thaï diabète",
      "tom yum diabète",
      "pad thaï diabétique",
    ],
  },
  Vietnam: {
    flag: "🇻🇳",
    tagline: "La cuisine vietnamienne, IG bas pour les diabétiques",
    intro:
      "La cuisine vietnamienne est parmi les plus saines au monde : herbes fraîches en abondance, peu de matière grasse, bouillons délicats, viandes maigres et beaucoup de crudités. Pho, bo bun, rouleaux de printemps : autant de plats naturellement à IG bas. Le seul point d'attention : les bouillons longs où le riz blanc et la sauce nuoc-mam sucrée peuvent grimper l'IG.",
    pillars: [
      "Herbes fraîches (menthe, coriandre, basilic thaï) à profusion",
      "Bouillons clairs, viande maigre, peu d'huile",
      "Rouleaux de printemps frais (galette de riz pas cuite : IG 50)",
      "Vermicelles de riz cuits al dente, en portion modérée",
    ],
    seoKeywords: [
      "recettes vietnamiennes diabétiques",
      "pho diabète",
      "bo bun IG bas",
      "cuisine vietnamienne diabète",
      "rouleaux de printemps diabète",
    ],
  },
  Grèce: {
    flag: "🇬🇷",
    tagline: "La cuisine grecque, IG bas pour les diabétiques",
    intro:
      "Le régime méditerranéen, dont la cuisine grecque est l'archétype, est considéré comme l'un des meilleurs au monde pour prévenir et accompagner le diabète. Huile d'olive, légumes, poissons, féta, légumineuses, herbes fraîches : tout est naturellement à IG bas. Tzatziki, moussaka, gigot d'agneau, salade grecque — un répertoire bénédiction pour les diabétiques.",
    pillars: [
      "Huile d'olive extra-vierge, pilier du régime méditerranéen",
      "Féta et yaourt grec : protéines et bonnes graisses",
      "Légumineuses (fava, gigantes) : IG 30-35",
      "Pain de seigle complet (carta di musica) en remplacement du pita blanc",
    ],
    seoKeywords: [
      "recettes grecques diabétiques",
      "cuisine grecque IG bas",
      "moussaka diabète",
      "tzatziki diabète",
      "salade grecque diabétique",
    ],
  },
  Turquie: {
    flag: "🇹🇷",
    tagline: "La cuisine turque, IG bas pour les diabétiques",
    intro:
      "La cuisine turque, à mi-chemin entre Méditerranée et Moyen-Orient, regorge de plats diabéto-compatibles : kebabs grillés, salade de berger, soupe aux lentilles, mezze de yaourt et concombre. Les pâtisseries (baklava, lokoum) restent les seuls vrais pièges — mais leurs alternatives à l'érythritol s'adaptent merveilleusement.",
    pillars: [
      "Grillades sur charbon : zéro féculent, pure protéine",
      "Soupe de lentilles (mercimek çorbası) : IG 30",
      "Yaourt nature en accompagnement (raïta turc) : ralentit la glycémie",
      "Boulgour complet (köfteli) au lieu du riz blanc",
    ],
    seoKeywords: [
      "recettes turques diabétiques",
      "cuisine turque IG bas",
      "kebab diabète",
      "boulgour diabétique",
      "mercimek diabète",
    ],
  },
  Japon: {
    flag: "🇯🇵",
    tagline: "La cuisine japonaise, IG bas pour les diabétiques",
    intro:
      "La cuisine japonaise traditionnelle (washoku) est souvent classée parmi les plus diabéto-compatibles : faible en graisses saturées, riche en poissons, légumes fermentés et algues. Sushis, sashimis, soupe miso, donburi : tout s'adapte facilement en remplaçant le riz blanc par du riz brun ou du konjac, et en limitant les sauces sucrées (teriyaki).",
    pillars: [
      "Konjac et shirataki : féculents quasi sans glucides",
      "Algues (wakame, nori) : minéraux et fibres",
      "Soupe miso : fermentation, bonne pour le microbiote",
      "Sashimi et poissons gras : oméga-3 abondants",
    ],
    seoKeywords: [
      "recettes japonaises diabétiques",
      "sushi IG bas diabète",
      "cuisine japonaise diabète",
      "miso diabète",
      "japonais diabétique",
    ],
  },
  Brésil: {
    flag: "🇧🇷",
    tagline: "La cuisine brésilienne, IG bas pour les diabétiques",
    intro:
      "Au-delà des stéréotypes, la cuisine brésilienne traditionnelle propose de nombreux plats diabéto-compatibles : feijoada (haricots noirs et viandes), peixe escabeche, salades tropicales. L'astuce : remplacer le riz blanc par du quinoa ou des haricots, et préférer la mandioca au manioc transformé.",
    pillars: [
      "Haricots noirs et rouges : IG 30, base de la feijoada",
      "Manioc cuit (IG 46) plutôt que farine de manioc raffinée",
      "Fruits tropicaux modérément (papaye, mangue) : citrus en priorité",
      "Acai bowl maison sans sirops sucrés ajoutés",
    ],
    seoKeywords: [
      "recettes brésiliennes diabétiques",
      "feijoada diabète",
      "cuisine brésilienne IG bas",
      "haricots noirs diabète",
    ],
  },
  Corée: {
    flag: "🇰🇷",
    tagline: "La cuisine coréenne, IG bas pour les diabétiques",
    intro:
      "La cuisine coréenne mise sur la fermentation (kimchi, doenjang), les légumes sautés (namul), les BBQ à charbon et les soupes claires. Naturellement riche en probiotiques et faible en sucre raffiné. Bibimbap, japchae, kimbap : à adapter avec riz brun et nouilles de patate douce.",
    pillars: [
      "Kimchi : probiotiques excellents pour le microbiote et la glycémie",
      "Nouilles de patate douce (japchae) : IG 50",
      "Riz brun au lieu du riz blanc dans les bibimbap",
      "Bouillons d'os longuement mijotés : reminéralisants",
    ],
    seoKeywords: [
      "recettes coréennes diabétiques",
      "bibimbap diabète",
      "kimchi diabète",
      "cuisine coréenne IG bas",
    ],
  },
  "États-Unis": {
    flag: "🇺🇸",
    tagline: "La cuisine américaine, IG bas pour les diabétiques",
    intro:
      "Souvent caricaturée comme la pire pour les diabétiques, la cuisine américaine cache de réels trésors : Cobb salad, chili con carne, cornbread complet, BBQ sans sauce sucrée, omelette de Denver. En privilégiant les recettes 'farm-to-table' plutôt que la fast-food, on retrouve une cuisine équilibrée et savoureuse.",
    pillars: [
      "Cobb salad : œufs, avocat, bacon, blue cheese — IG très bas",
      "Chili con carne aux haricots rouges : IG 30",
      "Burgers maison sur pain complet de seigle",
      "Pas de sirop d'érable / sodas : eau pétillante au citron",
    ],
    seoKeywords: [
      "recettes américaines diabétiques",
      "cuisine américaine IG bas",
      "Cobb salad diabète",
      "burger diabétique",
    ],
  },
  Portugal: {
    flag: "🇵🇹",
    tagline: "La cuisine portugaise, IG bas pour les diabétiques",
    intro:
      "Cousine de la cuisine méditerranéenne, la gastronomie portugaise est généreuse, marine et terrienne : bacalhau (morue), sardines grillées, caldo verde, pulpo à la galicienne. La plupart des plats sont compatibles avec un régime diabétique, à condition d'éviter les pastéis de nata classiques.",
    pillars: [
      "Bacalhau (morue séchée) : protéine maigre par excellence",
      "Caldo verde : soupe aux choux et pommes de terre allégées",
      "Huile d'olive du Douro, herbes méditerranéennes",
      "Sardines grillées : oméga-3 et calcium",
    ],
    seoKeywords: [
      "recettes portugaises diabétiques",
      "bacalhau diabète",
      "cuisine portugaise IG bas",
      "caldo verde diabète",
    ],
  },
  Allemagne: {
    flag: "🇩🇪",
    tagline: "La cuisine allemande, IG bas pour les diabétiques",
    intro:
      "La cuisine allemande, longtemps réputée lourde, possède des piliers parfaits pour les diabétiques : choucroute (probiotiques), pain de seigle complet (IG 40), saucisses de gibier, poissons fumés. Les ajustements sont minimes : limiter pommes de terre, préférer le rye bread au pain blanc, et éviter les desserts à base de pâte sucrée.",
    pillars: [
      "Choucroute fermentée : probiotiques et fibres",
      "Pain de seigle complet (Pumpernickel) : IG 40",
      "Saucisses de gibier ou de poulet maigre",
      "Salade de chou rouge à la pomme : IG modéré",
    ],
    seoKeywords: [
      "recettes allemandes diabétiques",
      "choucroute diabète",
      "cuisine allemande IG bas",
      "pain de seigle diabète",
    ],
  },
  "Royaume-Uni": {
    flag: "🇬🇧",
    tagline: "La cuisine britannique, IG bas pour les diabétiques",
    intro:
      "La cuisine britannique moderne, libérée de ses clichés gras, propose plein de plats diabéto-compatibles : porridge d'avoine, fish & chips au four (avec poisson cuit vapeur), ragoûts d'agneau, scrambled eggs aux fines herbes. La clé : éviter le fish & chips frit et les puddings au sucre raffiné.",
    pillars: [
      "Porridge d'avoine au lait d'amande : petit-déj IG 55",
      "Poissons grillés (saumon, maquereau, hareng) : oméga-3",
      "Ragoûts d'agneau aux légumes racines (panais, navet)",
      "Tea time : thé noir non sucré + scones à la farine complète",
    ],
    seoKeywords: [
      "recettes anglaises diabétiques",
      "cuisine britannique IG bas",
      "porridge diabète",
      "fish & chips diabétique",
    ],
  },
};

/** Generic fallback for any country not yet curated. */
export function getCountryEditorial(country) {
  if (!country) return null;
  if (COUNTRY_EDITORIAL[country]) return COUNTRY_EDITORIAL[country];
  return {
    flag: "🌍",
    tagline: `La cuisine ${country.toLowerCase()}, IG bas pour les diabétiques`,
    intro: `Découvrez nos recettes traditionnelles ${country.toLowerCase()} repensées pour les personnes diabétiques. Chaque plat est adapté à un index glycémique bas, en gardant l'authenticité et les saveurs originales — pour permettre aux diabétiques de continuer à explorer les cuisines du monde sans renoncer au plaisir.`,
    pillars: [
      "Adaptation IG bas conservant l'identité culturelle",
      "Substituts naturels au sucre raffiné",
      "Féculents complets à index glycémique modéré",
      "Conseils diététiques validés pour diabète type 1 et type 2",
    ],
    seoKeywords: [
      `recettes ${country.toLowerCase()} diabétiques`,
      `cuisine ${country.toLowerCase()} IG bas`,
      `${country.toLowerCase()} diabète`,
    ],
  };
}
